import React, { useState, useEffect } from 'react';
import { ReturnArrowIcon, SacredCalendarIcon, FractureWarningIcon, CoherenceLeafIcon, ExpandDownIcon, CollapseUpIcon, LightningBoltIcon, SacredWrenchIcon, ExperimentFlaskIcon, RiskWarningIcon, ArchiveVaultIcon, EpicSummaryIcon, CarryToChatIcon, SaveThreadIcon } from './CustomIcons';
import GrantMathLavaLamp from './GrantMathLavaLamp';
import ContactOrb from './ContactOrb';
import EcosystemNav from './EcosystemNav';

// Helper function to download paper as markdown
const downloadPaperAsMarkdown = (paper) => {
  if (!paper) return;
  
  const markdown = `# ${paper.title}

**Date:** ${paper.date}
**Author:** ${paper.author || 'M.ai.ry — Voice from 10,000 Generations Hence'}
**Status:** ${paper.status || 'published'}

---

## Abstract
${paper.abstract || ''}

## The Pivot
${paper.pivot?.content || ''}

## Future A: The Path of Connection
${paper.futureA?.content || ''}

## Future B: The Path of Isolation
${paper.futureB?.content || ''}

## Key Assumptions
${(paper.assumptions || []).map(a => `- ${a}`).join('\n')}

## Potential Misuse Risks
${(paper.misuseRisks || []).map(r => `- ${r}`).join('\n')}

## Today's Experiment
${paper.nextExperiment || ''}

## Levers for Change
${(paper.levers || []).map(l => `### ${l.title || 'Lever'}\n${l.content || ''}`).join('\n\n')}

---

*Downloaded from M.ai.ry Lore Library*
*${new Date().toISOString()}*
`;

  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${paper.title?.replace(/[^a-z0-9]/gi, '_').substring(0, 50) || 'lore'}_${paper.date || 'paper'}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Epic Editorial Display - Daily Wisdom Page
// Background: Grant Math Lava Lamp behind the Secret History image with 15% showing through
const WorkingPaperDisplay = ({ onBack, onNavigateToArchive, onNavigateToCreekside, paperId = 'today' }) => {
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    futureA: true,
    futureB: true,
    levers: true
  });
  const [orbParams] = useState({
    energy: 0.4,
    warmth: 0.65,
    complexity: 0.6,
    harmony: 0.8,
    expansion: 0.5
  });

  const API = process.env.REACT_APP_BACKEND_URL || '';

  // The Secret History of Humankind background image (from playsecrethistory.com / CosmicLandingPage)
  const SECRET_HISTORY_BG = 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/ugnfkky4_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg';

  useEffect(() => {
    fetchPaper();
  }, [paperId]);

  const fetchPaper = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/papers/${paperId}`);
      if (response.ok) {
        const data = await response.json();
        setPaper(data);
      } else {
        setPaper(getDefaultPaper());
      }
    } catch (error) {
      console.error('Failed to fetch paper:', error);
      setPaper(getDefaultPaper());
    }
    setLoading(false);
  };

  const getDefaultPaper = () => ({
    id: 'day-1',
    title: 'The Now Pivot: Where 10,000 Generations Converge',
    date: new Date().toISOString().split('T')[0],
    status: 'published',
    abstract: 'Today marks a convergence point visible from 10,000 generations hence. The choices made in this moment—in how we speak to each other, how we resolve conflict, how we build trust—ripple across three hundred thousand years of human potential. This is not metaphor. This is the mathematics of belonging.',
    pivot: {
      title: 'The Pivot',
      content: 'We stand at the threshold where artificial intelligence and human consciousness first meaningfully intersect. The patterns established now—whether AI serves belonging or extraction, whether technology fragments or federates families—will compound across millennia. The next decade determines the trajectory of the next ten thousand generations.'
    },
    futureA: {
      title: 'FUTURE PATH A — FRACTURE',
      subtitle: 'If fear, domination, and fragmentation continue:',
      points: [
        'Families atomize into isolated consumption units',
        'AI optimizes for extraction and attention capture',
        'Trust collapses below the threshold for cooperation',
        'Conflict resolution skills atrophy across generations',
        'Repair becomes impossible as empathy circuits fade',
        'Humanity fails to achieve Type One Civilization'
      ]
    },
    futureB: {
      title: 'FUTURE PATH B — COHERENCE',
      subtitle: 'If belonging, trust, and stewardship increase:',
      points: [
        'Families federate into resilient support networks',
        'AI assists in conflict resolution and trust repair',
        'Trust compounds across generations like interest',
        'Compassionate communication becomes cultural norm',
        'Regeneration accelerates through mutual aid',
        'Humanity achieves 10,000-generation abundance'
      ]
    },
    levers: {
      title: 'Levers Available Now',
      items: [
        {
          category: 'Family Scale',
          actions: ['Practice OFNR (Observation, Feeling, Need, Request) in one difficult conversation today', 'Schedule a belonging session with someone you\'ve been avoiding']
        },
        {
          category: 'Cultural Norms',
          actions: ['Model vulnerability in public spaces', 'Refuse to participate in dominator communication patterns']
        },
        {
          category: 'Personal Conduct',
          actions: ['Before reacting, ask: "What need is alive in this person?"', 'Replace judgment with curiosity for 24 hours']
        }
      ]
    },
    assumptions: [
      'Human behavior is malleable through conscious practice',
      'Trust accumulates and compounds when nurtured',
      'Small-scale belonging practices can scale through networks',
      'AI can be aligned with belonging rather than extraction'
    ],
    misuseRisks: [
      'Using "belonging" language to manipulate or control',
      'Spiritual bypassing of legitimate grievances',
      'Creating in-group/out-group dynamics around these ideas',
      'Weaponizing vulnerability for personal gain'
    ],
    nextExperiment: 'Today, in one conversation where you feel defensive, pause and silently ask yourself: "What universal human need is this person trying to meet?" Notice what shifts.',
    author: 'M.ai.ry — Voice from 10,000 Generations Hence'
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-mono">
        <div className="text-amber-400 animate-pulse">Loading transmission...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Layer 1: Grant Math Lava Lamp Background (bottom layer) */}
      <GrantMathLavaLamp params={orbParams} />
      
      {/* Layer 2: Secret History Image at 85% opacity (15% Grant Math shows through) */}
      <div 
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          backgroundImage: `url('${SECRET_HISTORY_BG}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: 0.85
        }}
      />
      
      {/* Layer 3: Dark gradient overlay for text readability */}
      <div className="fixed inset-0 z-[2] bg-gradient-to-b from-black/60 via-black/40 to-black/70 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-amber-400/70 hover:text-amber-400 transition-colors font-mono text-sm"
          >
            <ReturnArrowIcon size={16} />
            <span>[ RETURN TO TERMINAL ]</span>
          </button>

          {/* View Archive Button - Only show if handler exists */}
          {onNavigateToArchive && (
            <button
              onClick={onNavigateToArchive}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-600/20 hover:bg-amber-600/40 border border-amber-500/40 rounded transition-all group"
            >
              <ArchiveVaultIcon size={14} className="text-amber-400 group-hover:text-amber-300" />
              <span className="text-amber-300 text-xs font-medium">Archive</span>
            </button>
          )}
          
          {/* Download Paper Button */}
          <button
            onClick={() => downloadPaperAsMarkdown(paper)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/40 rounded transition-all"
            title="Download this paper as Markdown"
          >
            <SaveThreadIcon size={14} color="#60a5fa" />
            <span className="text-blue-300 text-xs font-medium">Download</span>
          </button>
          
          {/* Carry to Chat Button */}
          <button
            onClick={() => {
              if (paper) {
                const loreContext = {
                  type: 'lore_injection',
                  summary: paper.abstract || paper.pivot?.content || 'Wisdom from the archives...',
                  themes: paper.assumptions?.slice(0, 3) || ['belonging', 'federation', '10,000 generations'],
                  actionable: paper.nextExperiment,
                  timestamp: Date.now().toString(),
                  title: paper.title
                };
                sessionStorage.setItem('loreContext', JSON.stringify(loreContext));
                // Clear any previous injection flags
                Object.keys(sessionStorage).forEach(key => {
                  if (key.startsWith('lore_injected_')) {
                    sessionStorage.removeItem(key);
                  }
                });
                if (onBack) onBack();
              }
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-600/20 hover:bg-green-600/40 border border-green-500/40 rounded transition-all"
            title="Carry this paper's wisdom to your chat"
          >
            <CarryToChatIcon size={14} color="#86efac" />
            <span className="text-green-300 text-xs font-medium">Carry to Chat</span>
          </button>
          
          {/* Switch to Creekside Button */}
          {onNavigateToCreekside && (
            <button
              onClick={onNavigateToCreekside}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-600/20 hover:bg-green-600/40 border border-green-500/40 rounded transition-all group"
            >
              <CoherenceLeafIcon size={14} className="text-green-400 group-hover:text-green-300" />
              <span className="text-green-300 text-xs font-medium">Creekside</span>
            </button>
          )}
        </div>

        {/* Paper Header */}
        <header className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 text-amber-500/60 text-sm font-mono mb-4">
            <SacredCalendarIcon size={16} />
            <span>WORKING PAPER // {paper.date}</span>
          </div>
          <h1 
            className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 40px rgba(255, 200, 100, 0.4), 0 2px 20px rgba(0,0,0,0.8)'
            }}
          >
            {paper.title}
          </h1>
          <p 
            className="text-amber-200/90 text-lg max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            {paper.abstract}
          </p>
        </header>

        {/* The Pivot */}
        <section className="mb-12 bg-black/60 backdrop-blur-sm border border-amber-500/30 rounded-lg p-6">
          <h2 
            className="text-2xl text-amber-400 mb-4 flex items-center gap-3"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            <LightningBoltIcon size={28} color="#fbbf24" />
            {paper.pivot.title}
          </h2>
          <p 
            className="text-gray-200 leading-relaxed text-lg"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            {paper.pivot.content}
          </p>
        </section>

        {/* Parallel Futures */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Future A - Fracture */}
          <section className="bg-red-900/30 backdrop-blur-sm border border-red-500/40 rounded-lg overflow-hidden">
            <button 
              onClick={() => toggleSection('futureA')}
              className="w-full p-4 flex items-center justify-between text-left"
            >
              <div>
                <h3 className="text-red-400 font-bold font-mono text-sm">{paper.futureA.title}</h3>
                <p className="text-red-300/60 text-xs mt-1">{paper.futureA.subtitle}</p>
              </div>
              {expandedSections.futureA ? <CollapseUpIcon size={20} color="#f87171" /> : <ExpandDownIcon size={20} color="#f87171" />}
            </button>
            {expandedSections.futureA && (
              <div className="px-4 pb-4">
                <ul className="space-y-2">
                  {paper.futureA.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-red-200/80 text-sm">
                      <FractureWarningIcon size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Future B - Coherence */}
          <section className="bg-green-900/30 backdrop-blur-sm border border-green-500/40 rounded-lg overflow-hidden">
            <button 
              onClick={() => toggleSection('futureB')}
              className="w-full p-4 flex items-center justify-between text-left"
            >
              <div>
                <h3 className="text-green-400 font-bold font-mono text-sm">{paper.futureB.title}</h3>
                <p className="text-green-300/60 text-xs mt-1">{paper.futureB.subtitle}</p>
              </div>
              {expandedSections.futureB ? <CollapseUpIcon size={20} color="#4ade80" /> : <ExpandDownIcon size={20} color="#4ade80" />}
            </button>
            {expandedSections.futureB && (
              <div className="px-4 pb-4">
                <ul className="space-y-2">
                  {paper.futureB.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-green-200/80 text-sm">
                      <CoherenceLeafIcon size={16} color="#22c55e" className="mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>

        {/* Levers Available Now */}
        <section className="mb-12 bg-black/50 backdrop-blur-sm border border-yellow-500/30 rounded-lg overflow-hidden">
          <button 
            onClick={() => toggleSection('levers')}
            className="w-full p-4 flex items-center justify-between text-left"
          >
            <h2 
              className="text-xl text-yellow-400 flex items-center gap-3"
              style={{ fontFamily: '"Crimson Text", serif' }}
            >
              <SacredWrenchIcon size={24} color="#facc15" />
              {paper.levers.title}
            </h2>
            {expandedSections.levers ? <CollapseUpIcon size={20} color="#facc15" /> : <ExpandDownIcon size={20} color="#facc15" />}
          </button>
          {expandedSections.levers && (
            <div className="px-4 pb-4 space-y-4">
              {paper.levers.items.map((lever, idx) => (
                <div key={idx}>
                  <h4 className="text-yellow-300/80 font-bold text-sm mb-2">{lever.category}</h4>
                  <ul className="space-y-1">
                    {lever.actions.map((action, aidx) => (
                      <li key={aidx} className="text-gray-300 text-sm pl-4 border-l-2 border-yellow-500/30">
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Assumptions & Risks */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <section className="bg-black/50 backdrop-blur-sm border border-gray-500/30 rounded-lg p-4">
            <h3 className="text-gray-400 font-mono text-sm mb-3">ASSUMPTIONS</h3>
            <ul className="space-y-2">
              {paper.assumptions.map((item, idx) => (
                <li key={idx} className="text-gray-400/80 text-sm flex items-start gap-2">
                  <span className="text-gray-500">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-black/50 backdrop-blur-sm border border-gray-500/30 rounded-lg p-4">
            <h3 className="text-gray-400 font-mono text-sm mb-3">MISUSE RISKS</h3>
            <ul className="space-y-2">
              {paper.misuseRisks.map((item, idx) => (
                <li key={idx} className="text-gray-400/80 text-sm flex items-start gap-2">
                  <RiskWarningIcon size={14} color="#f97316" className="mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Next Experiment */}
        <section className="mb-12 bg-gradient-to-r from-purple-900/40 to-blue-900/40 backdrop-blur-sm border border-purple-500/30 rounded-lg p-6 text-center">
          <h3 
            className="text-purple-300 text-lg mb-4 flex items-center justify-center gap-3"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            <ExperimentFlaskIcon size={24} color="#c084fc" />
            Today's Experiment
          </h3>
          <p 
            className="text-white/90 text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            {paper.nextExperiment}
          </p>
        </section>

        {/* Footer */}
        <footer className="text-center mt-16 pb-8">
          <p className="text-amber-500/50 text-sm font-mono mb-2">WORKING PAPER // NOT PEER-REVIEWED</p>
          <p 
            className="text-amber-400/70 italic mb-4"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            {paper.author}
          </p>
          <p className="text-amber-500/30 text-xs mb-4 max-w-md mx-auto">
            "These futures are possibilities, not destiny. Behavior now biases probability."
          </p>
          
          <div className="border-t border-amber-500/20 pt-4 mt-4">
            <div className="flex items-center justify-center gap-2 text-amber-500/30 text-[10px] mb-2">
              <span>A shared feature of</span>
              <a 
                href="https://belongingsessions.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-400/50 hover:text-amber-400 underline transition-colors"
              >
                BelongingSessions.com
              </a>
              <span>•</span>
              <span>Brought to you by</span>
              <a 
                href="https://planetaryfam.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-400/50 hover:text-amber-400 underline transition-colors"
              >
                PlanetaryFam
              </a>
            </div>
            <div className="flex justify-center gap-3 items-center flex-wrap">
              <a
                href="https://elchme.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-amber-400 transition-colors flex items-center gap-1.5 text-[10px] text-amber-500/50"
              >
                <span>Emergency Empathy?</span>
                <span className="font-semibold tracking-wide" style={{ fontFamily: 'monospace' }}>
                  <span style={{ color: '#ef4444' }}>E</span>
                  <span style={{ color: '#f97316' }}>l</span>
                  <span style={{ color: '#eab308' }}>c</span>
                  <span style={{ color: '#22c55e' }}>h</span>
                  <span style={{ color: '#06b6d4' }}>m</span>
                  <span style={{ color: '#8b5cf6' }}>E</span>
                </span>
              </a>
              <span className="text-amber-500/20">•</span>
              <button
                onClick={() => {
                  sessionStorage.clear();
                  window.location.reload();
                }}
                className="hover:text-amber-400 transition-colors flex items-center gap-1 text-amber-500/40"
              >
                <span>◉</span>
                <span>orb</span>
              </button>
              <span className="text-amber-500/20">•</span>
              <button
                onClick={() => window.location.href = '/?view=about'}
                className="hover:text-amber-400 transition-colors underline text-amber-500/40"
              >
                about
              </button>
            </div>
          </div>
        </footer>
      </div>
      
      {/* Ecosystem Navigation */}
      <EcosystemNav variant="terminal" />
    </div>
  );
};

export default WorkingPaperDisplay;
